// e1c/src/components/BrandingManager.js
// Author: Gemini
// Timestamp: 2025-09-03
import React, { useState, useEffect } from 'react'; // Import useEffect
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'; // Import useQuery
import {
    Box, Typography, Paper, Grid, TextField, Button,
    CircularProgress, Alert, Tooltip, IconButton
} from '@mui/material';
import ColorLensIcon from '@mui/icons-material/ColorLens';

import authFetch from '../utils/authFetch';
import logger from '../utils/logger';

// API call to GET existing branding data
const getBrandingApi = async () => {
    logger.debug("BrandingManager: Calling API to get branding...");
    const data = await authFetch('/branding/');
    logger.info("BrandingManager: Get branding API response:", data);
    return data;
};

// API call for general branding data updates (PUT request)
const updateBrandingApi = async (brandingData) => {
    logger.debug("BrandingManager: Calling API to update branding...", brandingData);
    const data = await authFetch('/branding/', {
        method: 'PUT',
        body: brandingData, // This should be a plain object
    });
    logger.info("BrandingManager: Update branding API response:", data);
    return data;
};

// API call for logo file upload
const uploadLogoApi = async (file) => {
    const formData = new FormData();
    formData.append('logo_file', file);
    const data = await authFetch('/branding/logo', {
        method: 'POST',
        body: formData,
    });
    return data;
};

// NEW: API call for hero image file upload
const uploadHeroImageApi = async (file) => {
    const formData = new FormData();
    formData.append('hero_image_file', file);
    const data = await authFetch('/branding/hero', {
        method: 'POST',
        body: formData,
    });
    return data;
};

// MODIFIED: Accept tenantId prop to enable the query
function BrandingManager({ tenantId }) {
    const queryClient = useQueryClient();

    // NEW: Fetch existing branding data to populate the form
    const { data: currentBranding, isLoading: isFetchingBranding } = useQuery({
        queryKey: ['tenant-branding', tenantId],
        queryFn: getBrandingApi,
        enabled: !!tenantId, // Only run query when tenantId is available
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });

    const [branding, setBranding] = useState({
        logo_url: '',
        hero_image_url: '',
        welcome_message: '',
        primary_theme_color: '#1976D2',
        secondary_theme_color: '#64B5F6',
    });
    const [logoFile, setLogoFile] = useState(null);
    const [heroFile, setHeroFile] = useState(null); // NEW: State for hero image file
    const [successMessage, setSuccessMessage] = useState('');
    const [fieldError, setFieldError] = useState('');

    // NEW: useEffect to populate the form state when data is loaded
    useEffect(() => {
        if (currentBranding) {
            setBranding({
                logo_url: currentBranding.logo_url || '',
                hero_image_url: currentBranding.hero_image_url || '',
                welcome_message: currentBranding.welcome_message || '',
                primary_theme_color: currentBranding.primary_theme_color || '#1976D2',
                secondary_theme_color: currentBranding.secondary_theme_color || '#64B5F6',
            });
        }
    }, [currentBranding]);


    // Mutations for API calls
    const updateBrandingMutation = useMutation({ mutationFn: updateBrandingApi });
    const uploadLogoMutation = useMutation({ mutationFn: uploadLogoApi });
    const uploadHeroImageMutation = useMutation({ mutationFn: uploadHeroImageApi });

    const isMutating = updateBrandingMutation.isPending || uploadLogoMutation.isPending || uploadHeroImageMutation.isPending;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBranding(prev => ({ ...prev, [name]: value }));
    };

    // Handler for logo file input change
    const handleLogoFileChange = (e) => {
        setLogoFile(e.target.files[0]);
    };

    // NEW: Handler for hero image file input change
    const handleHeroFileChange = (e) => {
        setHeroFile(e.target.files[0]);
    };

    // REFACTORED: Combined submission logic to handle all updates correctly
    const handleSubmit = async (event) => {
        event.preventDefault();
        setSuccessMessage('');
        setFieldError('');

        try {
            let finalBrandingData = { ...branding };

            // Step 1: Upload logo file if a new one is selected
            if (logoFile) {
                const logoUploadResponse = await uploadLogoMutation.mutateAsync(logoFile);
                finalBrandingData.logo_url = logoUploadResponse.logo_url;
                setLogoFile(null); // Clear file input state after upload
            }

            // Step 2: Upload hero image file if a new one is selected
            if (heroFile) {
                const heroUploadResponse = await uploadHeroImageMutation.mutateAsync(heroFile);
                finalBrandingData.hero_image_url = heroUploadResponse.hero_image_url;
                setHeroFile(null); // Clear file input state after upload
            }

            // Step 3: Update only the text and color fields with a clean payload.
            const textAndColorData = {
                welcome_message: finalBrandingData.welcome_message,
                primary_theme_color: finalBrandingData.primary_theme_color,
                secondary_theme_color: finalBrandingData.secondary_theme_color,
            };
            await updateBrandingMutation.mutateAsync(textAndColorData);

            setSuccessMessage("Branding updated successfully!");
            // Use the same query key to invalidate, ensuring consistency
            queryClient.invalidateQueries({ queryKey: ['tenant-branding', tenantId] });
            setTimeout(() => setSuccessMessage(''), 5000);

        } catch (error) {
            logger.error("Error saving branding:", error);
            const detail = error.response?.data?.detail || "Failed to update branding. Please check your inputs.";
            setFieldError(detail);
        }
    };

    const isLoading = isFetchingBranding || isMutating;

    return (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
            <Typography component="h2" variant="h5" gutterBottom>
                Branding & Customization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customize the look and feel of the patient-facing portal.
            </Typography>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                </Alert>
            )}
            {fieldError && (
                 <Alert severity="error" sx={{ mb: 2 }}>
                    {fieldError}
                </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Welcome Message"
                            name="welcome_message"
                            multiline
                            rows={3}
                            value={branding.welcome_message}
                            onChange={handleInputChange}
                            placeholder="e.g., Welcome to the Cardiac Surgery Patient Portal!"
                            disabled={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Upload New Logo</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoFileChange}
                                style={{ display: 'none' }}
                                id="logo-file-upload"
                                disabled={isLoading}
                            />
                            <label htmlFor="logo-file-upload">
                                <Button variant="outlined" component="span" disabled={isLoading}>
                                    Choose File
                                </Button>
                            </label>
                            <Typography variant="body2" sx={{ ml: 2, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {logoFile ? logoFile.name : 'No file chosen'}
                            </Typography>
                        </Box>
                        {branding.logo_url && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Current: {branding.logo_url}</Typography>}
                    </Grid>
                    {/* NEW: Field for Hero Image Upload */}
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Upload New Hero Image</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleHeroFileChange}
                                style={{ display: 'none' }}
                                id="hero-file-upload"
                                disabled={isLoading}
                            />
                            <label htmlFor="hero-file-upload">
                                <Button variant="outlined" component="span" disabled={isLoading}>
                                    Choose File
                                </Button>
                            </label>
                            <Typography variant="body2" sx={{ ml: 2, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {heroFile ? heroFile.name : 'No file chosen'}
                            </Typography>
                        </Box>
                        {branding.hero_image_url && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Current: {branding.hero_image_url}</Typography>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Primary Theme Color"
                            name="primary_theme_color"
                            value={branding.primary_theme_color}
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="Preview Color" placement="right">
                                        <IconButton>
                                            <ColorLensIcon sx={{ color: branding.primary_theme_color || 'inherit' }} />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            }}
                            disabled={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Secondary Theme Color"
                            name="secondary_theme_color"
                            value={branding.secondary_theme_color}
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="Preview Color" placement="right">
                                        <IconButton>
                                            <ColorLensIcon sx={{ color: branding.secondary_theme_color || 'inherit' }} />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            }}
                            disabled={isLoading}
                        />
                    </Grid>
                </Grid>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3, position: 'relative' }}
                    disabled={isLoading}
                >
                    Save Branding
                    {isLoading && (
                        <CircularProgress
                            size={24}
                            sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }}
                        />
                    )}
                </Button>
            </Box>
        </Paper>
    );
}

export default BrandingManager;